import { useEffect, useState } from 'react';

import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconPlus, IconTrash } from '@tabler/icons-react';

import {
  EXTERNAL_PROVIDERS,
  providerLabel,
  toProviderAgreementInput,
  type ExternalProvider,
  type ProviderAgreement,
  type ProviderAgreementInput,
} from '@/types/provider-cost';
import { formatMoney } from '@/utils';
import { COST_CURRENCIES, isDecimal, toCents } from './format';

type PricingMode = 'shared' | 'per-line';

interface LineValues {
  service: string;
  unitKey: string;
  allowance: number | string;
  priceShare: string;
  overageRate: string;
}

interface AgreementFormValues {
  provider: string;
  startsOn: string;
  endsOn: string;
  price: string;
  currency: string;
  notes: string;
  pricing: PricingMode;
  lines: LineValues[];
}

const BLANK_LINE: LineValues = {
  service: '',
  unitKey: '',
  allowance: '',
  priceShare: '',
  overageRate: '',
};

export interface ProviderAgreementModalProps {
  opened: boolean;
  onClose: () => void;
  /** The agreement being edited; absent to record a new one. */
  agreement?: ProviderAgreement | null;
  onCreate: (input: ProviderAgreementInput) => Promise<unknown>;
  onReplace: (id: string, input: ProviderAgreementInput) => Promise<unknown>;
}

function initialValues(agreement: ProviderAgreement | null | undefined): AgreementFormValues {
  if (!agreement) {
    return {
      provider: '',
      startsOn: '',
      endsOn: '',
      price: '',
      currency: 'GBP',
      notes: '',
      pricing: 'shared',
      lines: [{ ...BLANK_LINE }],
    };
  }
  const input = toProviderAgreementInput(agreement);
  return {
    provider: input.provider,
    startsOn: input.startsOn,
    endsOn: input.endsOn,
    price: input.price,
    currency: input.currency,
    notes: input.notes ?? '',
    pricing: agreement.sharedPool ? 'shared' : 'per-line',
    lines: input.lines.map((line) => ({
      service: line.service,
      unitKey: line.unitKey,
      allowance: line.allowance,
      priceShare: line.priceShare ?? '',
      overageRate: line.overageRate ?? '',
    })),
  };
}

/** What is wrong with the lines as a whole, or null. */
export function linesProblem(values: AgreementFormValues): string | null {
  if (values.lines.length === 0) {
    return 'Add at least one line';
  }
  const keys = values.lines.map((l) => `${l.service.trim()}/${l.unitKey.trim()}`);
  if (new Set(keys).size !== keys.length) {
    return 'Each service and unit may appear on only one line';
  }
  if (values.pricing === 'per-line' && isDecimal(values.price, 2)) {
    const total = values.lines.reduce((sum, l) => sum + toCents(l.priceShare), 0);
    if (total !== toCents(values.price)) {
      return 'The line shares must add up to the price';
    }
  }
  return null;
}

function toInput(values: AgreementFormValues): ProviderAgreementInput {
  return {
    provider: values.provider as ExternalProvider,
    startsOn: values.startsOn,
    endsOn: values.endsOn,
    price: values.price,
    currency: values.currency,
    notes: values.notes || null,
    lines: values.lines.map((line) => ({
      service: line.service,
      unitKey: line.unitKey,
      allowance: Number(line.allowance),
      priceShare: values.pricing === 'per-line' ? line.priceShare : null,
      overageRate: line.overageRate || null,
    })),
  };
}

/**
 * Record or edit a prepaid agreement: what was paid, over which period, for
 * which allowance of each kind of unit. The server divides the price over the
 * allowance and puts each line's rate on the rate card; saving replaces them.
 */
export function ProviderAgreementModal({
  opened,
  onClose,
  agreement,
  onCreate,
  onReplace,
}: Readonly<ProviderAgreementModalProps>) {
  const editing = Boolean(agreement);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<AgreementFormValues>({
    initialValues: initialValues(agreement),
    validate: {
      provider: (v) => (v ? null : 'Choose a provider'),
      startsOn: (v) => (v ? null : 'Choose a start date'),
      endsOn: (v, values) => {
        if (!v) {
          return 'Choose an end date';
        }
        return v < values.startsOn ? 'Ends before it starts' : null;
      },
      price: (v) => (isDecimal(v, 2) ? null : 'A price such as 10000.00'),
      lines: {
        service: (v) => (v.trim() ? null : 'Required'),
        unitKey: (v) => (v.trim() ? null : 'Required'),
        allowance: (v) => (Number(v) > 0 && Number.isInteger(Number(v)) ? null : 'Whole number'),
        priceShare: (v, values) =>
          values.pricing === 'per-line' && !isDecimal(v, 2) ? 'Required' : null,
        overageRate: (v) => (v === '' || isDecimal(v) ? null : 'Up to 6 places'),
      },
    },
  });

  useEffect(() => {
    if (opened) {
      form.setValues(initialValues(agreement));
      form.resetDirty();
      setSubmitError(null);
    }
    // Reset only when the modal opens or the agreement being edited changes.
  }, [opened, agreement]);

  const values = form.getValues();
  const problem = linesProblem(values);
  const sharesTotal = values.lines.reduce((sum, l) => sum + toCents(l.priceShare), 0) / 100;

  const submit = form.onSubmit(async (submitted) => {
    if (linesProblem(submitted)) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const input = toInput(submitted);
      if (agreement) {
        await onReplace(agreement.id, input);
      } else {
        await onCreate(input);
      }
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not save the agreement');
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editing ? 'Edit agreement' : 'Record agreement'}
      size="xl"
    >
      <form onSubmit={submit}>
        <Stack>
          <Group grow align="flex-start">
            <Select
              label="Provider"
              data={EXTERNAL_PROVIDERS.map((p) => ({ value: p, label: providerLabel(p) }))}
              {...form.getInputProps('provider')}
            />
            <TextInput type="date" label="Starts" {...form.getInputProps('startsOn')} />
            <TextInput type="date" label="Ends" {...form.getInputProps('endsOn')} />
          </Group>
          <Group grow align="flex-start">
            <TextInput
              label="Price paid"
              placeholder="10000.00"
              inputMode="decimal"
              {...form.getInputProps('price')}
            />
            <Select
              label="Currency"
              data={[...COST_CURRENCIES]}
              allowDeselect={false}
              {...form.getInputProps('currency')}
            />
          </Group>
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Pricing
            </Text>
            <SegmentedControl
              data={[
                { value: 'shared', label: 'One price, shared pool' },
                { value: 'per-line', label: 'Priced per line' },
              ]}
              {...form.getInputProps('pricing')}
            />
            <Text size="xs" c="dimmed">
              {values.pricing === 'shared'
                ? 'Every unit, whatever its kind, is worth the price divided by the total allowance.'
                : "Each line's rate is its share of the price divided by its allowance."}
            </Text>
          </Stack>

          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Service</Table.Th>
                <Table.Th>Unit</Table.Th>
                <Table.Th>Allowance</Table.Th>
                {values.pricing === 'per-line' ? <Table.Th>Share of price</Table.Th> : null}
                <Table.Th>Overage per unit</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {values.lines.map((_, index) => (
                <Table.Tr key={form.key(`lines.${index}`)}>
                  <Table.Td>
                    <TextInput
                      aria-label={`Service ${index + 1}`}
                      placeholder="screen"
                      {...form.getInputProps(`lines.${index}.service`)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      aria-label={`Unit ${index + 1}`}
                      placeholder="checks"
                      {...form.getInputProps(`lines.${index}.unitKey`)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <NumberInput
                      aria-label={`Allowance ${index + 1}`}
                      min={1}
                      allowDecimal={false}
                      thousandSeparator=","
                      {...form.getInputProps(`lines.${index}.allowance`)}
                    />
                  </Table.Td>
                  {values.pricing === 'per-line' ? (
                    <Table.Td>
                      <TextInput
                        aria-label={`Share of price ${index + 1}`}
                        inputMode="decimal"
                        {...form.getInputProps(`lines.${index}.priceShare`)}
                      />
                    </Table.Td>
                  ) : null}
                  <Table.Td>
                    <TextInput
                      aria-label={`Overage per unit ${index + 1}`}
                      placeholder="None"
                      inputMode="decimal"
                      {...form.getInputProps(`lines.${index}.overageRate`)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label="Remove line">
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={`Remove line ${index + 1}`}
                        disabled={values.lines.length === 1}
                        onClick={() => form.removeListItem('lines', index)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Group justify="space-between">
            <Button
              variant="subtle"
              leftSection={<IconPlus size={16} />}
              onClick={() => form.insertListItem('lines', { ...BLANK_LINE })}
            >
              Add line
            </Button>
            {values.pricing === 'per-line' ? (
              <Text size="sm" c={problem ? 'red' : 'dimmed'} data-testid="shares-total">
                Shares {formatMoney(sharesTotal, values.currency)} of{' '}
                {formatMoney(values.price || 0, values.currency)}
              </Text>
            ) : null}
          </Group>
          {problem && form.isTouched() ? (
            <Text size="sm" c="red">
              {problem}
            </Text>
          ) : null}

          <Textarea label="Notes" autosize minRows={2} {...form.getInputProps('notes')} />
          {submitError ? (
            <Alert color="red" title="Not saved">
              {submitError}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={problem !== null}>
              {editing ? 'Save agreement' : 'Record agreement'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
